import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { IAPIListResult, IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { andWhere, buildQuery, CombinedFilter, FilterFn, noop, Ops, orderBy, OrderVariant, orWhere, page, select, where } from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { ICreateAsset } from "./interfaces/create-asset.interface";
import AssetAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/assets/asset/asset.entity";
import { IUpdateAsset } from "./interfaces/update-asset.interface";


const apiUrl = env.API_AVE_ASSET;
const moduleName = "assets";

@Injectable({
    providedIn: "root",
})
export class AssetService extends AbstractService<AssetAttributes, ICreateAsset, IUpdateAsset> {

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * Obtiene activos con paginación y filtros.
     *
     * Estrategia de búsqueda:
     * - Sin búsqueda: Un solo objeto con filtros base
     * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
     *
     * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
     *
     * @param options Pagination options
     * @param filter Filters to apply (contains group array)
     * @param status Status filter (array)
     * @param companyId Company filter
     * @param group Asset group filter (array)
     * @returns Paginated data
     *
     * Search fields: code, name
     * Array filters: group (from filter and parameter), status
     * Single filters: companyId
     */
    getAllPaginateAsset(
        options: IPagination<AssetAttributes>,
        filter: any,
        status?,
        companyId?,
        group?
    ): Observable<IPaginationResult<AssetAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, filtro de compañía, grupo, estados.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<AssetAttributes>[] => {
            const deletedFilter: FilterFn<AssetAttributes> = params.useOrWhere
                ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                : where('_deleted', Ops.eq(false));   // Primer objeto del array

            return [
                deletedFilter,
                companyId
                    ? andWhere('companies.id' as keyof AssetAttributes, Ops.eq(companyId))
                    : noop(),
                group && group.length > 0
                    ? andWhere('assetGroup.code' as keyof AssetAttributes, Ops.in(...group))
                    : noop(),
                filter?.group && Array.isArray(filter.group) && filter.group.length > 0
                    ? andWhere('assetGroup.code' as keyof AssetAttributes, Ops.in(...filter.group))
                    : noop(),
                status && status.length > 0
                    ? andWhere('statusId', Ops.in(...status))
                    : noop()
            ];
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
        const searchByCodeQueryObject: FilterFn<AssetAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
            hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
        const searchByNameQueryObject: FilterFn<AssetAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
            andWhere("name", Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<AssetAttributes>(
            // Expandimos ambos objetos en el buildQuery
            ...searchByCodeQueryObject,
            ...searchByNameQueryObject,

            // Ordenamiento y paginación
            orderBy(
                options.sort ?? "name",
                options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiUrl +
            `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<AssetAttributes>>(API_URL);
    }

    /**
     * Find to combo box
     * @returns Data
    */
    findToComboBox(): Observable<IRequestResult<AssetAttributes[]>> {
        const query = buildQuery<AssetAttributes>(
            where("statusId", StatusEnum.ACTIVE),
            select(["_id", "code", "name", "companies"]),
            orderBy("code", OrderVariant.Asc)
        );

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IRequestResult<AssetAttributes[]>>(API_URL);
    }
}
